import {
	PUBLIC_API_HOST,
	PUBLIC_CA_API_HOST,
	PUBLIC_CDN_HOST,
	PUBLIC_ENABLE_API_PROXY,
} from "$env/static/public";
import { Observable, map } from "rxjs";
import { getItem, observe, setItem } from "./local-store";
import { env } from "$env/dynamic/public";

const KEY = 'config-override';

export class IConf<ValueType> {

	private key: string;
	hasOverride$: Observable<boolean>;
	value$: Observable<ValueType>;

	constructor(
		name: string,
		private defaultValue: ValueType,
	) {
		this.key = `${KEY}:${name}`;

		this.hasOverride$ = observe<ValueType>(this.key).pipe(
			map(item => typeof item.value !== 'undefined' && item.value !== null),
		)
		
		this.value$ = observe<ValueType>(this.key).pipe(
			map(item => item.value ?? defaultValue),
		)
	}

	/**
	 * Sets or clears the override value of the configur.
	 * @param value The override value. If not provided, the override is cleared.
	 * @returns The override value, or the default value if no override is set.
	 */
	override(value?: ValueType) {
		setItem(this.key, value);
		return getItem<ValueType>(this.key) ?? this.defaultValue;
	}

	get value() {
		return getItem<ValueType>(this.key) ?? this.defaultValue;
	}

}

export const payProvider = env.PUBLIC_PAY_PROVIDER || 'payu';

const cdnHost = new IConf('cdnHost', PUBLIC_CDN_HOST);
export const cdnHost$ = cdnHost.value$;
export const cdnHost_hasOverride$ = cdnHost.hasOverride$;
export const overrideCdnHost: typeof cdnHost.override = v => cdnHost.override(v);

const apiHost = new IConf('apiHost', env.PUBLIC_API_HOST || PUBLIC_API_HOST);
export const apiHost$ = apiHost.value$;
export const apiHost_hasOverride$ = apiHost.hasOverride$;
export const overrideApiHost: typeof apiHost.override = v => apiHost.override(v);

const caApiHostDefault = env.PUBLIC_CA_API_HOST || PUBLIC_CA_API_HOST;
const caApiHost = new IConf('caApiHost', caApiHostDefault);
export const caApiHost$ = caApiHost.value$;
export const caApiHost_hasOverride$ = caApiHost.hasOverride$;
export const overrideCaApiHost: typeof caApiHost.override = v => caApiHost.override(v);

export type CaApiPath = [string, string];
const caApiPaths = new IConf<CaApiPath[]>('caApiPaths', [['/restaurant',caApiHostDefault],['/grocery',caApiHostDefault]]);
export const caApiPaths$ = caApiPaths.value$;
export const caApiPaths_hasOverride$ = caApiPaths.hasOverride$;
export const overrideCaApiPaths: typeof caApiPaths.override = v => caApiPaths.override(v?.map(p =>[...p])); // clone

const _proxyEnabled = new IConf('proxyEnabled', !!PUBLIC_ENABLE_API_PROXY);
export const proxyEnabled = {
	value: () => _proxyEnabled.value,
	value$: _proxyEnabled.value$,
	hasOverride$: _proxyEnabled.hasOverride$,
	override: (v => _proxyEnabled.override(v)) as typeof _proxyEnabled.override,
}

const _liCache = new IConf('liCache', true);
export const liCache = {
	value: () => _liCache.value,
	value$: _liCache.value$,
	hasOverride$: _liCache.hasOverride$,
	override: (v => _liCache.override(v)) as typeof _liCache.override,
}

const _debugCache = new IConf('debugCache', true);
export const debugCache = {
	value: () => _debugCache.value,
	value$: _debugCache.value$,
	hasOverride$: _debugCache.hasOverride$,
	override: (v => _debugCache.override(v)) as typeof _debugCache.override,
}

